import { Component, OnInit, OnDestroy, OnChanges, Input, Output, EventEmitter, ViewChild, ElementRef, HostListener, ChangeDetectorRef } from '@angular/core';
import { FilePreviewService } from '../../../services/file-preview-service/file-preview.service';
import { Subscription } from 'rxjs';
import { LoggerService } from '../../../services/logger-services/logger.service';
import { PdfViewerComponent } from 'ng2-pdf-viewer';
import { FilePreviewRequest, FilePreviewData } from '../../models/file-preview.models';
import { renderAsync } from 'docx-preview';

/**
 * FilePreviewComponent handles the display of uploaded files
 * in a side panel similar to the source panel.
 * Supports PDF, DOCX, and TXT files with read-only preview.
 */
@Component({
  selector: 'app-file-preview',
  templateUrl: './file-preview.component.html',
  styleUrls: ['./file-preview.component.scss']
})
export class FilePreviewComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef;
  @ViewChild(PdfViewerComponent, { static: false }) pdfViewer?: PdfViewerComponent;
  @ViewChild('docxContainer', { static: false }) docxContainer?: ElementRef;

  // Input Properties
  @Input() previewRequest: FilePreviewRequest | null = null;
  @Input() isVisible: boolean = false;

  // Output Events
  @Output() closePanelEvent = new EventEmitter<void>();

  // Public Properties
  public isLoading: boolean = false;
  public previewData: FilePreviewData | null = null;
  public errorMessage: string = '';
  public zoomLevel: number = 100;
  public currentPage: number = 1;
  public totalPages: number = 0;

  // PDF-specific properties
  public pdfSrc: string | null = null;
  public pdfZoom: number = 1.0;
  public pdfLoaded: boolean = false;
  public firstPageRendered: boolean = false;
  public showPdfViewer: boolean = false;

  // DOCX-specific properties
  public docxLoaded: boolean = false;
  public docxBlobUrl: string | null = null;

  // TXT-specific properties
  public txtHtml: string | null = null;

  // Citation mode properties
  public isCitationMode: boolean = false;
  public citationText: string = '';
  public citationPage: number = 0;

  // Subscriptions
  private readonly subscriptions: Subscription = new Subscription();

  // Constants
  private readonly MIN_ZOOM = 50;
  private readonly MAX_ZOOM = 200;
  private readonly ZOOM_STEP = 10;

  constructor(
    private readonly filePreviewService: FilePreviewService,
    private readonly loggerService: LoggerService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  /**
   * Angular lifecycle hook - Component initialization
   */
  ngOnInit(): void {
    try {
      if (this.previewRequest) {
        this.loadFilePreview();
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'FilePreviewComponent - ngOnInit');
    }
  }

  /**
   * Angular lifecycle hook - Component destruction
   */
  ngOnDestroy(): void {
    try {
      this.subscriptions.unsubscribe();
      this.cleanupBlobUrl();
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'FilePreviewComponent - ngOnDestroy');
    }
  }

  /**
   * Watch for changes in preview request
   */
  ngOnChanges(): void {
    try {
      if (this.previewRequest && this.isVisible) {
        this.loadFilePreview();
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'FilePreviewComponent - ngOnChanges');
    }
  }

  /**
   * Load file preview based on preview request
   */
  private loadFilePreview(): void {
    try {
      if (!this.previewRequest) {
        this.errorMessage = 'No file to preview';
        return;
      }

      this.isLoading = true;
      this.errorMessage = '';
      this.previewData = null;
      this.pdfLoaded = false;
      this.firstPageRendered = false;
      this.showPdfViewer = false;
      this.docxLoaded = false;
      this.txtHtml = null;
      
      // Setup citation mode if requested
      this.isCitationMode = this.previewRequest.citationMode || false;
      this.citationText = this.previewRequest.highlightText || '';
      this.citationPage = this.previewRequest.targetPage || 0;
      
      this.cleanupBlobUrl();

      const subscription = this.filePreviewService.getFilePreview(this.previewRequest)
        .subscribe({
          next: (data: FilePreviewData) => {
            this.handlePreviewData(data);
          },
          error: (error: any) => {
            this.handlePreviewError(error);
          }
        });

      this.subscriptions.add(subscription);
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'loadFilePreview');
      this.handlePreviewError(error);
    }
  }

  /**
   * Handle successful preview data
   */
  private handlePreviewData(data: FilePreviewData): void {
    try {
      this.isLoading = false;
      
      if (data.error) {
        this.errorMessage = data.error;
        return;
      }

      this.previewData = data;

      // Set PDF source if it's a PDF
      if (data.type === 'pdf' && data.blobUrl) {
        this.pdfSrc = data.blobUrl;
        // Reset page to 1, but let it initialize first
        this.currentPage = 1;
        this.totalPages = 0;
        
        // Delay showing the PDF viewer to allow proper initialization
        setTimeout(() => {
          this.showPdfViewer = true;
          this.cdr.detectChanges();
        }, 100);
      }

      // Set DOCX source if it's a DOCX
      if (data.type === 'docx' && data.blobUrl) {
        this.docxBlobUrl = data.blobUrl;
        this.currentPage = 1;
        this.totalPages = 0;
        
        // Render DOCX after a short delay
        setTimeout(() => {
          this.renderDocx(data.blobUrl!);
        }, 200);
      }

      // Handle TXT with citation mode
      if (data.type === 'txt' && data.content) {
        // If in citation mode, highlight the text
        if (this.isCitationMode && this.citationText) {
          setTimeout(() => {
            this.highlightTxtText(this.citationText);
          }, 300);
        }
      }

      // Reset zoom for all file types
      this.zoomLevel = 100;
      this.pdfZoom = 1.0;
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'handlePreviewData');
      this.errorMessage = 'Failed to display preview';
    }
  }

  /**
   * Handle preview errors
   */
  private handlePreviewError(error: any): void {
    this.isLoading = false;
    this.errorMessage = error?.message || 'Failed to load file preview';
    this.loggerService.logError(this.formatError(error), 'handlePreviewError');
  }

  /**
   * Close the preview panel
   */
  public closePanel(): void {
    try {
      this.cleanupBlobUrl();
      this.closePanelEvent.emit();
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'closePanel');
    }
  }

  /**
   * Zoom in
   */
  public zoomIn(): void {
    try {
      if (this.zoomLevel < this.MAX_ZOOM) {
        this.zoomLevel += this.ZOOM_STEP;
        this.updatePdfZoom();
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'zoomIn');
    }
  }

  /**
   * Zoom out
   */
  public zoomOut(): void {
    try {
      if (this.zoomLevel > this.MIN_ZOOM) {
        this.zoomLevel -= this.ZOOM_STEP;
        this.updatePdfZoom();
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'zoomOut');
    }
  }

  /**
   * Reset zoom to 100%
   */
  public resetZoom(): void {
    try {
      this.zoomLevel = 100;
      this.updatePdfZoom();
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'resetZoom');
    }
  }

  /**
   * Update PDF zoom level
   */
  private updatePdfZoom(): void {
    this.pdfZoom = this.zoomLevel / 100;
  }


  /**
   * Format file size for display
   */
  public formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file icon based on type
   */
  public getFileIcon(): string {
    if (!this.previewData) return '';
    
    switch (this.previewData.type) {
      case 'pdf':
        return './assets/icons/pdf-icon.svg';
      case 'docx':
        return './assets/icons/word-icon.svg';
      case 'txt':
        return './assets/icons/text.svg';
      default:
        return '';
    }
  }

  /**
   * Clean up blob URL to prevent memory leaks
   */
  private cleanupBlobUrl(): void {
    try {
      if (this.pdfSrc) {
        this.filePreviewService.revokeBlobUrl(this.pdfSrc);
        this.pdfSrc = null;
      }
      if (this.docxBlobUrl) {
        this.filePreviewService.revokeBlobUrl(this.docxBlobUrl);
        this.docxBlobUrl = null;
      }
      if (this.previewData?.blobUrl) {
        this.filePreviewService.revokeBlobUrl(this.previewData.blobUrl);
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'cleanupBlobUrl');
    }
  }

  /**
   * Handle PDF load complete
   */
  public onPdfLoadComplete(pdf: any): void {
    try {
      this.totalPages = pdf?.numPages || 0;
      this.pdfLoaded = true;
      this.loggerService.logInfo(`PDF loaded: ${this.totalPages} pages`, 'onPdfLoadComplete');
      
      // Ensure currentPage is valid
      if (this.currentPage > this.totalPages) {
        this.currentPage = 1;
      }

      // If in citation mode or targetPage is specified, jump to it after load
      if (this.isCitationMode && this.citationText && this.citationPage) {
        this.loggerService.logInfo(`Jumping to page ${this.citationPage} with highlight: "${this.citationText}"`, 'onPdfLoadComplete');
        setTimeout(() => {
          this.jumpToPageWithHighlight(this.citationPage, this.citationText);
        }, 1000);
      } else if (this.previewRequest?.targetPage && this.previewRequest.targetPage <= this.totalPages) {
        this.loggerService.logInfo(`Jumping to target page ${this.previewRequest.targetPage}`, 'onPdfLoadComplete');
        setTimeout(() => {
          this.jumpToPageWithHighlight(this.previewRequest!.targetPage!, this.previewRequest?.highlightText);
        }, 1000);
      }
      
      this.cdr.detectChanges();
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'onPdfLoadComplete');
    }
  }

  /**
   * Jump to a specific page and optionally highlight text
   * @param pageNumber - The page number to jump to (1-indexed)
   * @param textToHighlight - Optional text to search and highlight on that page
   */
  public jumpToPageWithHighlight(pageNumber: number, textToHighlight?: string): void {
    try {
      if (!this.pdfLoaded || pageNumber < 1 || pageNumber > this.totalPages) {
        this.loggerService.logError('Invalid page number or PDF not loaded', 'jumpToPageWithHighlight');
        return;
      }

      // Set the current page
      this.currentPage = pageNumber;
      this.cdr.detectChanges();

      // If text highlighting is requested, wait for page to render then highlight
      if (textToHighlight) {
        setTimeout(() => {
          this.highlightTextOnPage(textToHighlight);
        }, 800);
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'jumpToPageWithHighlight');
    }
  }

  /**
   * Highlight text on the current page using PDF.js find functionality
   * @param searchText - The text to search and highlight
   */
  private highlightTextOnPage(searchText: string): void {
    try {
      if (!this.pdfViewer || !this.pdfViewer.pdfViewer || !searchText) {
        this.loggerService.logError('PDF viewer not ready or no search text', 'highlightTextOnPage');
        return;
      }

      this.loggerService.logInfo(`Attempting to highlight text: "${searchText}"`, 'highlightTextOnPage');
      const pdfViewerInstance = this.pdfViewer.pdfViewer;
      
      // Access the find controller if available
      if ((pdfViewerInstance as any).pdfFindController) {
        this.loggerService.logInfo('Using PDF.js find controller', 'highlightTextOnPage');
        const findController = (pdfViewerInstance as any).pdfFindController;
        
        // Execute the search
        findController.executeCommand('find', {
          query: searchText,
          caseSensitive: false,
          highlightAll: true,
          findPrevious: false
        });
      } else {
        // Fallback: Use custom CSS-based highlighting
        this.loggerService.logInfo('Using CSS-based highlighting fallback', 'highlightTextOnPage');
        setTimeout(() => {
          this.highlightTextWithCSS(searchText);
        }, 500);
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'highlightTextOnPage');
      // Try CSS fallback on error
      setTimeout(() => {
        this.highlightTextWithCSS(searchText);
      }, 500);
    }
  }

  /**
   * Fallback method to highlight text using CSS (for when find controller is unavailable)
   * @param searchText - The text to highlight
   */
  private highlightTextWithCSS(searchText: string): void {
    try {
      this.loggerService.logInfo(`CSS highlighting started for: "${searchText}"`, 'highlightTextWithCSS');
      
      // Get the text layer elements
      const textLayers = document.querySelectorAll('.file-preview__pdf-viewer .textLayer');
      this.loggerService.logInfo(`Found ${textLayers.length} text layers`, 'highlightTextWithCSS');
      
      let foundFirst = false;
      let matchCount = 0;
      
      textLayers.forEach((textLayer, layerIndex) => {
        const textSpans = textLayer.querySelectorAll('span');
        this.loggerService.logInfo(`Layer ${layerIndex}: Found ${textSpans.length} text spans`, 'highlightTextWithCSS');
        
        textSpans.forEach((span) => {
          const text = span.textContent || '';
          // Try both exact match and partial match
          if (text.toLowerCase().includes(searchText.toLowerCase()) || 
              text.toLowerCase() === searchText.toLowerCase()) {
            matchCount++;
            this.loggerService.logInfo(`Match #${matchCount} found: "${text}"`, 'highlightTextWithCSS');
            
            // Add citation-ready yellow highlight styling
            (span as HTMLElement).style.backgroundColor = '#FFFF00'; // Pure yellow
            (span as HTMLElement).style.borderRadius = '3px';
            (span as HTMLElement).style.padding = '2px 4px';
            (span as HTMLElement).style.boxShadow = '0 0 0 2px rgba(255, 255, 0, 0.3)';
            (span as HTMLElement).style.fontWeight = '500';
            span.classList.add('citation-highlight');
            
            // Scroll to the first occurrence
            if (!foundFirst) {
              foundFirst = true;
              this.loggerService.logInfo('Scrolling to first match', 'highlightTextWithCSS');
              setTimeout(() => {
                span.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }, 100);
            }
          }
        });
      });
      
      this.loggerService.logInfo(`Highlighting complete. Found ${matchCount} matches`, 'highlightTextWithCSS');
      if (matchCount === 0) {
        this.loggerService.logError(`No matches found for "${searchText}"`, 'highlightTextWithCSS');
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'highlightTextWithCSS');
    }
  }

  /**
   * Copy citation text to clipboard
   */
  public copyCitation(): void {
      if (!this.citationText) {
        return;
      }

      const fileName = this.previewRequest?.fileName || 'Document';
      
      // Format citation based on file type
      let citationString: string;
      if (this.previewData?.type === 'txt') {
        // TXT files don't have pages
        citationString = `"${this.citationText}"\n\nSource: ${fileName}`;
      } else {
        // PDF and DOCX have page numbers
        citationString = `"${this.citationText}"\n\nSource: ${fileName}, Page ${this.citationPage}`;
      }
      
      // Copy to clipboard
      navigator.clipboard.writeText(citationString).then(() => {
        this.loggerService.logInfo('Citation copied to clipboard', 'copyCitation');
        // You can add a toast notification here if you have one
        alert('Citation copied to clipboard!');
      }).catch((error: any) => {
        this.loggerService.logError(this.formatError(error), 'copyCitation');
      });
  }

  /**
   * Handle PDF page rendered
   */
  public onPageRendered(event: any): void {
    try {
      // Mark first page as rendered
      if (!this.firstPageRendered) {
        this.firstPageRendered = true;
        this.cdr.detectChanges();
      }
      // The page number is controlled by user actions (next/prev buttons) and citation mode
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'onPageRendered');
    }
  }

  /**
   * Handle PDF text layer rendered
   */
  public onTextLayerRendered(event: any): void {
    // Text layer rendered successfully
  }

  /**
   * Handle PDF error
   */
  public onPdfError(error: any): void {
    this.loggerService.logError(this.formatError(error), 'onPdfError');
    this.errorMessage = 'Failed to load PDF file';
  }

  /**
   * Render DOCX file using docx-preview library
   */
  private async renderDocx(blobUrl: string): Promise<void> {
    try {
      if (!this.docxContainer || !this.docxContainer.nativeElement) {
        this.loggerService.logError('DOCX container not found', 'renderDocx');
        return;
      }

      this.loggerService.logInfo('Rendering DOCX file', 'renderDocx');
      
      // Fetch the blob
      const response = await fetch(blobUrl);
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();

      // Clear the container
      const container = this.docxContainer.nativeElement;
      container.innerHTML = '';

      // Render the DOCX
      await renderAsync(arrayBuffer, container, undefined, {
        className: 'docx-preview-content',
        inWrapper: true,
        ignoreWidth: true,
        ignoreHeight: false,
        ignoreFonts: false,
        breakPages: true,
        ignoreLastRenderedPageBreak: false,
        experimental: false,
        trimXmlDeclaration: true,
        useBase64URL: false,
        renderChanges: false,
        renderHeaders: true,
        renderFooters: true,
        renderFootnotes: true,
        renderEndnotes: true,
      });

      this.docxLoaded = true;
      
      // Count pages in the rendered DOCX
      setTimeout(() => {
        this.countDocxPages();
        
        // If in citation mode, highlight text
        if (this.isCitationMode && this.citationText) {
          setTimeout(() => {
            this.highlightDocxText(this.citationText);
          }, 500);
        }
      }, 500);

      this.loggerService.logInfo('DOCX rendered successfully', 'renderDocx');
      this.cdr.detectChanges();
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'renderDocx');
      this.errorMessage = 'Failed to render DOCX file';
      this.cdr.detectChanges();
    }
  }

  /**
   * Count pages in rendered DOCX
   */
  private countDocxPages(): void {
    try {
      if (!this.docxContainer || !this.docxContainer.nativeElement) {
        return;
      }

      // Count page breaks in the rendered DOCX
      const pages = this.docxContainer.nativeElement.querySelectorAll('.docx-wrapper section');
      this.totalPages = pages.length || 1;
      this.currentPage = 1;
      
      this.loggerService.logInfo(`DOCX has ${this.totalPages} pages`, 'countDocxPages');
      this.cdr.detectChanges();
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'countDocxPages');
    }
  }

  /**
   * Highlight text in DOCX
   */
  private highlightDocxText(searchText: string): void {
    try {
      if (!this.docxContainer || !this.docxContainer.nativeElement) {
        return;
      }

      this.loggerService.logInfo(`Highlighting DOCX text: "${searchText}"`, 'highlightDocxText');
      
      const container = this.docxContainer.nativeElement;
      const allTextNodes: Node[] = [];
      
      // Get all text nodes
      const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      let node;
      while (node = walker.nextNode()) {
        allTextNodes.push(node);
      }
      
      let matchCount = 0;
      let firstMatch: HTMLElement | null = null;

      // Search and highlight
      allTextNodes.forEach((textNode) => {
        const text = textNode.textContent || '';
        const lowerText = text.toLowerCase();
        const lowerSearch = searchText.toLowerCase();
        
        if (lowerText.includes(lowerSearch)) {
          const parent = textNode.parentElement;
          if (parent) {
            const index = lowerText.indexOf(lowerSearch);
            const before = text.substring(0, index);
            const match = text.substring(index, index + searchText.length);
            const after = text.substring(index + searchText.length);
            
            const highlightSpan = document.createElement('span');
            highlightSpan.style.backgroundColor = '#FFFF00';
            highlightSpan.style.padding = '2px 4px';
            highlightSpan.style.borderRadius = '3px';
            highlightSpan.style.boxShadow = '0 0 0 2px rgba(255, 255, 0, 0.3)';
            highlightSpan.style.fontWeight = '500';
            highlightSpan.classList.add('citation-highlight');
            highlightSpan.textContent = match;
            
            const fragment = document.createDocumentFragment();
            if (before) fragment.appendChild(document.createTextNode(before));
            fragment.appendChild(highlightSpan);
            if (after) fragment.appendChild(document.createTextNode(after));
            
            parent.replaceChild(fragment, textNode);
            
            matchCount++;
            if (!firstMatch) {
              firstMatch = highlightSpan;
            }
          }
        }
      });
      
      this.loggerService.logInfo(`Found ${matchCount} matches in DOCX`, 'highlightDocxText');
      
      // Scroll to first match
      if (firstMatch) {
        setTimeout(() => {
          firstMatch!.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'highlightDocxText');
    }
  }

  /**
   * Highlight text in TXT file
   */
  private highlightTxtText(searchText: string): void {
    try {
      if (!this.previewData || !this.previewData.content) {
        return;
      }

      this.loggerService.logInfo(`Highlighting TXT text: "${searchText}"`, 'highlightTxtText');

      // Get the original content (should be plain text)
      const content = typeof this.previewData.content === 'string' 
        ? this.previewData.content 
        : String(this.previewData.content);
      
      const lowerContent = content.toLowerCase();
      const lowerSearch = searchText.toLowerCase();
      
      const index = lowerContent.indexOf(lowerSearch);
      
      if (index !== -1) {
        // Found the text - highlight it
        const before = content.substring(0, index);
        const match = content.substring(index, index + searchText.length);
        const after = content.substring(index + searchText.length);
        
        // Create highlighted HTML - escape text content but not HTML tags
        // User content is escaped via escapeHtml(), only hardcoded span tag is injected
        this.txtHtml = 
          this.escapeHtml(before) + 
          `<span class="txt-highlight" style="background-color: #FFFF00; padding: 2px 4px; border-radius: 3px; box-shadow: 0 0 0 2px rgba(255, 255, 0, 0.3); font-weight: 500;">${this.escapeHtml(match)}</span>` + 
          this.escapeHtml(after);
        
        this.cdr.detectChanges();
        
        // Scroll to the highlighted text
        setTimeout(() => {
          const highlightElement = document.querySelector('.txt-highlight');
          if (highlightElement) {
            highlightElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 200);
        
        this.loggerService.logInfo('TXT text highlighted successfully', 'highlightTxtText');
      } else {
        this.loggerService.logInfo(`Text "${searchText}" not found in TXT file`, 'highlightTxtText');
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'highlightTxtText');
    }
  }

  /**
   * Escape HTML special characters
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Navigate to next page (PDF)
   */
  public nextPage(): void {
    try {
      if (this.pdfLoaded && this.firstPageRendered && this.currentPage < this.totalPages) {
        this.currentPage++;
        this.cdr.detectChanges();
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'nextPage');
    }
  }

  /**
   * Navigate to previous page (PDF)
   */
  public previousPage(): void {
    try {
      if (this.pdfLoaded && this.firstPageRendered && this.currentPage > 1) {
        this.currentPage--;
        this.cdr.detectChanges();
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'previousPage');
    }
  }

  /**
   * Format error for logging
   */
  private formatError(error: any): any {
    if (error?.error instanceof ErrorEvent) {
      return { message: error.error.message };
    } else {
      return {
        status: error?.status,
        message: error?.message,
        error: error?.error
      };
    }
  }

  /**
   * Keyboard shortcuts
   */
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (!this.isVisible) return;

    try {
      // Zoom shortcuts
      if (event.ctrlKey || event.metaKey) {
        if (event.key === '+' || event.key === '=') {
          event.preventDefault();
          this.zoomIn();
        } else if (event.key === '-') {
          event.preventDefault();
          this.zoomOut();
        } else if (event.key === '0') {
          event.preventDefault();
          this.resetZoom();
        }
      }

      // Page navigation for PDFs
      if (this.previewData?.type === 'pdf') {
        if (event.key === 'ArrowLeft' || event.key === 'PageUp') {
          event.preventDefault();
          this.previousPage();
        } else if (event.key === 'ArrowRight' || event.key === 'PageDown') {
          event.preventDefault();
          this.nextPage();
        }
      }

      // Close with Escape
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closePanel();
      }
    } catch (error: any) {
      this.loggerService.logError(this.formatError(error), 'handleKeyboardEvent');
    }
  }
}

